import { Button, Icon, Input, Modal } from "@legal-500/legal500-design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  ActiveKeyspacePointer,
  Family,
  KeyspaceDirectoryEntry,
} from "@legal-500/redis-db-manager-types";
import { FAMILIES } from "@legal-500/redis-db-manager-types";
import { type DbManagerKeyspace, dbManagerService } from "../../../api/dbManager.service.js";
import { ROUTES } from "../../../routes.js";
import EmptyState from "../../../components/EmptyState/index.js";
import LoadingState from "../../../components/LoadingState/index.js";
import { MakeActiveModal } from "./MakeActiveModal.js";
import { PageHeader } from "../../../components/PageHeader/index.js";
import { CopyLabelButton } from "./CopyLabelButton.js";
import { PreviewLinkModal } from "./PreviewLinkModal.js";
import { RemoveKeyspaceModal } from "./RemoveKeyspaceModal.js";
import { SlotReleaseDetails } from "./SlotReleaseDetails.js";
import { SlotTargetedFlagChip, SlotTargetedFlagList } from "./SlotTargetedFlagList.js";
import { StatusBadge } from "./StatusBadge.js";
import { TriggerSyncModal } from "./TriggerSyncModal.js";
import { UserLabel } from "./UserLabel.js";
import { useSlotTargetedFlags } from "./useSlotTargetedFlags.js";
import { NON_CORE_SYNC_DISABLED_TOOLTIP } from "./releaseDefinitionUtils.js";
import { SearchIndexMetadata } from "./SearchIndexLine.js";
import { makeActiveButtonState } from "./searchIndexStatus.js";
import {
  computeSlotStatus,
  formatDurationMs,
  formatTimestamp,
  humanizeBytes,
} from "./slotStatus.js";
import styles from "./DbManager.module.css";

const PAGE_SIZE = 100;
const MAX_AUTO_CHUNKS = 20;

type PageTypeStat = {
  pageType: string;
  successful: number;
  failed: number;
  totalBytes: number;
  avgDurationMs: number;
  lastSyncedAt: number;
};

type PageTypeCounts = {
  totalKeys: number;
  sampledKeys: number;
  counts: Record<string, number>;
};

type Toast = { id: number; type: "success" | "error"; message: string };

export default function SlotDetailPage() {
  const { keyspace: keyspaceParam } = useParams<{ keyspace: string }>();
  const keyspace = keyspaceParam ?? "";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The family is normally supplied by the caller (index.tsx knows the entry's
  // family before navigating here). It's only absent for a bookmarked/shared
  // URL created before family-scoping existed, or a hand-typed URL - see the
  // fallback resolution in the bootstrap effect below.
  const familyFromUrl = searchParams.get("family");
  const [entry, setEntry] = useState<DbManagerKeyspace | null>(null);
  const [allKeyspaces, setAllKeyspaces] = useState<KeyspaceDirectoryEntry[]>([]);
  const [activeKeyspaces, setActiveKeyspaces] = useState<
    Record<Family, ActiveKeyspacePointer | null>
  >(
    () =>
      Object.fromEntries(FAMILIES.map(family => [family, null])) as Record<
        Family,
        ActiveKeyspacePointer | null
      >,
  );
  const entryFamilyRef = useRef<string | undefined>(undefined);
  const targetedFlags = useSlotTargetedFlags().flagsFor(keyspace);
  const [allPageTypes, setAllPageTypes] = useState<string[]>([]);
  const [pageTypeStats, setPageTypeStats] = useState<PageTypeStat[] | null>(null);
  const [pageTypeCounts, setPageTypeCounts] = useState<PageTypeCounts | null>(null);
  const [memory, setMemory] = useState<{ keyCount: number; datasetBytes: number } | null>(null);
  const [previewLinkOpen, setPreviewLinkOpen] = useState(false);
  const [triggerSyncOpen, setTriggerSyncOpen] = useState(false);
  const [makeActiveOpen, setMakeActiveOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [matchInput, setMatchInput] = useState("*");
  const [search, setSearch] = useState({ pattern: "*" });
  const [keys, setKeys] = useState<Array<{ key: string; type: string }>>([]);
  const [done, setDone] = useState(false);
  const [scanning, setScanning] = useState(false);
  const cursorRef = useRef("0");
  const scanControllerRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [keyValue, setKeyValue] = useState<{
    type: string;
    value: unknown;
    truncated: boolean;
    ttlSeconds: number;
  } | null>(null);
  const [keyValueLoading, setKeyValueLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeqRef = useRef(0);

  const pushToast = useCallback((type: Toast["type"], message: string) => {
    toastSeqRef.current += 1;
    const id = toastSeqRef.current;
    setToasts(prev => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const loadEntry = useCallback(
    async (familyHint?: string) => {
      try {
        const targetFamily = familyHint ?? entryFamilyRef.current ?? "core";
        const response = await dbManagerService.getKeyspace(keyspace, targetFamily);
        entryFamilyRef.current = response.keyspace.family;
        setEntry(response.keyspace);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [keyspace],
  );

  const loadDirectoryState = useCallback(async () => {
    try {
      const [keyspacesResponse, pageTypes] = await Promise.all([
        dbManagerService.listKeyspaces(),
        dbManagerService.listPageTypes().catch(() => [] as string[]),
      ]);
      setAllKeyspaces(keyspacesResponse.keyspaces);
      setActiveKeyspaces(keyspacesResponse.activeKeyspaces);
      setAllPageTypes(pageTypes);
      return keyspacesResponse.keyspaces;
    } catch (err) {
      pushToast("error", err instanceof Error ? err.message : String(err));
      return null;
    }
  }, [pushToast]);

  const loadMemory = useCallback(async () => {
    try {
      const response = await dbManagerService.getKeyspaceMemory();
      const memEntry = response.keyspaces.find(k => k.keyspace === keyspace);
      setMemory(
        memEntry ? { keyCount: memEntry.keyCount, datasetBytes: memEntry.datasetBytes } : null,
      );
    } catch (err) {
      setMemory(null);
      pushToast("error", err instanceof Error ? err.message : String(err));
    }
  }, [keyspace, pushToast]);

  const loadPageTypeStats = useCallback(async () => {
    try {
      const response = await dbManagerService.getKeyspacePageTypeStats(keyspace);
      setPageTypeStats(response.pageTypes);
    } catch (err) {
      setPageTypeStats(null);
      pushToast("error", err instanceof Error ? err.message : String(err));
    }
  }, [keyspace, pushToast]);

  const loadPageTypeCounts = useCallback(async () => {
    try {
      const response = await dbManagerService.getKeyspacePageTypeCounts(keyspace);
      setPageTypeCounts({
        totalKeys: response.totalKeys,
        sampledKeys: response.sampledKeys,
        counts: response.counts,
      });
    } catch (err) {
      setPageTypeCounts(null);
      pushToast("error", err instanceof Error ? err.message : String(err));
    }
  }, [keyspace, pushToast]);

  const runScan = useCallback(
    async (pattern: string) => {
      scanControllerRef.current?.abort();
      const controller = new AbortController();
      scanControllerRef.current = controller;
      setScanning(true);
      let gathered = 0;
      try {
        for (let chunk = 0; chunk < MAX_AUTO_CHUNKS; chunk += 1) {
          const response = await dbManagerService.listKeyspaceKeys(
            keyspace,
            { match: pattern, cursor: cursorRef.current, count: PAGE_SIZE },
            controller.signal,
          );
          if (controller.signal.aborted) return;
          gathered += response.keys.length;
          setKeys(prev => [...prev, ...response.keys]);
          cursorRef.current = response.nextCursor;
          setDone(response.done);
          if (response.done || gathered >= PAGE_SIZE) return;
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        pushToast("error", err instanceof Error ? err.message : String(err));
        setDone(false);
      } finally {
        if (!controller.signal.aborted) {
          scanControllerRef.current = null;
          setScanning(false);
        }
      }
    },
    [keyspace, pushToast],
  );

  const stopScan = () => {
    scanControllerRef.current?.abort();
    scanControllerRef.current = null;
    setScanning(false);
  };

  useEffect(() => {
    if (!keyspace) return;
    void (async () => {
      const list = await loadDirectoryState();

      // The family is normally already known from the ?family= query param set
      // by the caller (index.tsx's onViewDetail knows the entry's real family
      // before navigating here). Trust that as the source of truth.
      if (familyFromUrl) {
        await loadEntry(familyFromUrl);
        return;
      }

      // Fallback for links that predate family-scoping (bookmarks, hand-typed
      // URLs): resolve the family from the unfiltered, cross-family directory
      // listing. Keyspace names aren't guaranteed unique across families
      // (createKeyspaceHandler's conflict check is per-family), so if more
      // than one family has an entry with this exact name, we can't safely
      // guess which one the user means - surface an error instead of
      // silently resolving to whichever happens to come first.
      const matches = (list ?? []).filter(k => k.keyspace === keyspace);
      if (matches.length > 1) {
        setError(
          `Keyspace "${keyspace}" exists under multiple families (${matches
            .map(m => m.family)
            .join(", ")}) and no family was specified in the URL. Open this ` +
            "slot from the Db Manager list instead of using this link directly.",
        );
        return;
      }
      await loadEntry(matches[0]?.family);
    })();
    loadMemory();
    loadPageTypeStats();
    loadPageTypeCounts();
  }, [
    keyspace,
    familyFromUrl,
    loadDirectoryState,
    loadEntry,
    loadMemory,
    loadPageTypeStats,
    loadPageTypeCounts,
  ]);

  useEffect(() => {
    if (!keyspace) return;
    setKeys([]);
    setDone(false);
    cursorRef.current = "0";
    void runScan(search.pattern);
    return () => scanControllerRef.current?.abort();
  }, [keyspace, search, runScan]);

  const handleSearch = () => {
    setSearch({ pattern: matchInput.trim() || "*" });
  };

  const handleSelectKey = useCallback(
    async (key: string) => {
      setSelectedKey(key);
      setKeyValue(null);
      setKeyValueLoading(true);
      try {
        const response = await dbManagerService.getKeyspaceKey(keyspace, key);
        setKeyValue({
          type: response.type,
          value: response.value,
          truncated: response.truncated,
          ttlSeconds: response.ttlSeconds,
        });
      } catch (err) {
        pushToast("error", err instanceof Error ? err.message : String(err));
        setSelectedKey(null);
      } finally {
        setKeyValueLoading(false);
      }
    },
    [keyspace, pushToast],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    if (deleteConfirm !== deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await dbManagerService.deleteKeyspaceKey(keyspace, deleteTarget);
      pushToast("success", `Deleted ${deleteTarget}`);
      setKeys(prev => prev.filter(k => k.key !== deleteTarget));
      if (selectedKey === deleteTarget) {
        setSelectedKey(null);
        setKeyValue(null);
      }
      setDeleteTarget(null);
      setDeleteConfirm("");
    } catch (err) {
      pushToast("error", err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteTarget, deleteConfirm, keyspace, selectedKey, pushToast]);

  const refreshDetailState = useCallback(() => {
    void loadEntry();
    void loadDirectoryState();
    void loadMemory();
    void loadPageTypeStats();
    void loadPageTypeCounts();
  }, [loadDirectoryState, loadEntry, loadMemory, loadPageTypeCounts, loadPageTypeStats]);

  const activePointer = entry ? activeKeyspaces[entry.family] : null;
  const isActive =
    !!entry && activePointer?.mode === "slot" && activePointer.slot === entry.keyspace;
  const computedStatus = entry ? computeSlotStatus(entry, isActive, allPageTypes) : null;
  const slotStatus = computedStatus?.status ?? null;
  const missingPageTypes = computedStatus?.missingPageTypes ?? [];
  const makeActive = entry ? makeActiveButtonState(entry) : null;

  if (!keyspace) {
    return (
      <div className={styles.wrapper}>
        <PageHeader title="Invalid slot" subtitle="Slot parameter missing" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={`legal500.com Data — ${keyspace}`}
        subtitle={
          entry
            ? `${entry.family}${entry.runId ? ` · run ${entry.runId.slice(0, 8)}` : ""}`
            : "Loading..."
        }
      >
        <div className={styles.headerActions}>
          <div className={styles.slotDetailHeaderBack}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.l500Data.redisLoading)}
            >
              Back
            </Button>
          </div>
        </div>
      </PageHeader>

      <section className={styles.slotDetailToolbar}>
        <div className={styles.slotDetailToolbarBody}>
          <div className={styles.slotDetailToolbarMeta}>
            {entry?.keyspace && (
              <div className={styles.slotDetailToolbarSlotLabel}>
                <code>{entry.keyspace}</code>
                <CopyLabelButton value={entry.keyspace} ariaLabel={`Copy slot ${entry.keyspace}`} />
              </div>
            )}
            <div className={styles.slotDetailToolbarTopRow}>
              {slotStatus && <StatusBadge status={slotStatus} />}
              {entry?.family && <span className={styles.slotDetailFamilyTag}>{entry.family}</span>}
              {targetedFlags.flagKeys.map(key => (
                <SlotTargetedFlagChip key={key} flagKey={key} />
              ))}
            </div>
          </div>

          <div className={styles.slotDetailActionPanel}>
            <div className={styles.slotDetailActionPrimaryGroup}>
              {entry?.keyspace && !isActive && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setTriggerSyncOpen(true)}
                  startIcon={<Icon icon="settings-cog" size="small" />}
                  disabled={entry.family !== "core"}
                  title={entry.family !== "core" ? NON_CORE_SYNC_DISABLED_TOOLTIP : undefined}
                >
                  Trigger sync
                </Button>
              )}
              {entry?.keyspace &&
                !isActive &&
                slotStatus !== "running" &&
                slotStatus !== "empty" && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setMakeActiveOpen(true)}
                    startIcon={<Icon icon="check" size="small" />}
                    disabled={makeActive?.disabled}
                    title={makeActive?.title}
                    aria-describedby={makeActive?.describedById}
                  >
                    Make active
                  </Button>
                )}
            </div>
            <div className={styles.slotDetailActionSecondaryGroup}>
              {entry?.keyspace && !isActive && (
                <Button
                  variant="text"
                  color="default"
                  onClick={() => setPreviewLinkOpen(true)}
                  startIcon={<Icon icon="copy" size="small" />}
                >
                  Preview link
                </Button>
              )}
              {entry?.keyspace && !isActive && slotStatus !== "running" && (
                <Button
                  variant="text"
                  color="error"
                  onClick={() => setRemoveOpen(true)}
                  startIcon={<Icon icon="cross" size="small" />}
                >
                  Remove
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {error && (
        <EmptyState
          variant="error"
          title="Failed to load slot"
          message={error}
          action={{ label: "Retry", onClick: () => loadEntry() }}
        />
      )}

      {entry && (
        <>
          <section className={styles.slotDetailSection}>
            <h2 className={styles.slotDetailHeading}>Metadata</h2>
            <dl className={styles.slotDetailMetadata}>
              <div>
                <dt>Status</dt>
                <dd>{entry.status}</dd>
              </div>
              <div>
                <dt>Slot</dt>
                <dd>
                  <code>{entry.keyspace}</code> <CopyLabelButton value={entry.keyspace} />
                </dd>
              </div>
              {entry.runId && (
                <div>
                  <dt>Run ID</dt>
                  <dd>
                    {entry.runId} <CopyLabelButton value={entry.runId} />
                  </dd>
                </div>
              )}
              <div>
                <dt>Started</dt>
                <dd>{formatTimestamp(entry.startedAt)}</dd>
              </div>
              <div>
                <dt>Finished</dt>
                <dd>{formatTimestamp(entry.finishedAt)}</dd>
              </div>
              <div>
                <dt>Duration</dt>
                <dd>{formatDurationMs(entry.totals?.durationMs)}</dd>
              </div>
              <div>
                <dt>Triggered by</dt>
                <dd>
                  <UserLabel value={entry.triggeredBy} />
                </dd>
              </div>
              <div>
                <dt>Doc keys (current)</dt>
                <dd>
                  {pageTypeCounts
                    ? (pageTypeCounts.counts.doc ?? 0).toLocaleString()
                    : (entry.totals?.docKeyCount?.toLocaleString() ?? "-")}
                </dd>
              </div>
              <div>
                <dt>Total keys (current)</dt>
                <dd>{memory ? memory.keyCount.toLocaleString() : "—"}</dd>
              </div>
              <div>
                <dt>Slot size (current)</dt>
                <dd>{memory ? humanizeBytes(memory.datasetBytes) : "—"}</dd>
              </div>
              <SearchIndexMetadata entry={entry} />
              {entry.source?.seededFromKeyspace !== undefined && (
                <div>
                  <dt>Seeded from</dt>
                  <dd>
                    <code>{entry.source.seededFromKeyspace}</code>
                  </dd>
                </div>
              )}
            </dl>
          </section>

          <section className={styles.slotDetailSection}>
            <h2 className={styles.slotDetailHeading}>Release</h2>
            <SlotReleaseDetails
              releaseDefinition={entry.releaseDefinition}
              projectionVersion={entry.projectionVersion}
            />
          </section>

          <section className={styles.slotDetailSection}>
            <h2 className={styles.slotDetailHeading}>Slot-only feature flags</h2>
            <SlotTargetedFlagList targetedFlags={targetedFlags} />
          </section>

          <section className={styles.slotDetailSection}>
            <h2 className={styles.slotDetailHeading}>Page-type stats (last sync)</h2>
            {pageTypeStats === null ? (
              <LoadingState message="Loading page-type stats..." />
            ) : pageTypeStats.length === 0 ? (
              <p className={styles.slotDetailEmpty}>
                No stats yet — run a sync against this slot to populate the snapshot.
              </p>
            ) : (
              <table className={styles.slotDetailTable}>
                <thead>
                  <tr>
                    <th>Page type</th>
                    <th className={styles.slotDetailColRight}>Count</th>
                    <th className={styles.slotDetailColRight}>Size</th>
                    <th className={styles.slotDetailColRight}>Avg / doc</th>
                    <th className={styles.slotDetailColRight}>Avg time</th>
                    <th className={styles.slotDetailColRight}>Failed</th>
                    <th>Last synced</th>
                  </tr>
                </thead>
                <tbody>
                  {pageTypeStats.map(stat => {
                    const avgBytes =
                      stat.successful > 0 ? Math.round(stat.totalBytes / stat.successful) : 0;
                    return (
                      <tr key={stat.pageType}>
                        <td>{stat.pageType}</td>
                        <td className={styles.slotDetailColRight}>
                          {stat.successful.toLocaleString()}
                        </td>
                        <td className={styles.slotDetailColRight}>
                          {humanizeBytes(stat.totalBytes)}
                        </td>
                        <td className={styles.slotDetailColRight}>{humanizeBytes(avgBytes)}</td>
                        <td className={styles.slotDetailColRight}>
                          {formatDurationMs(stat.avgDurationMs)}
                        </td>
                        <td className={styles.slotDetailColRight}>
                          {stat.failed > 0 ? stat.failed.toLocaleString() : "—"}
                        </td>
                        <td>{formatTimestamp(stat.lastSyncedAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section className={styles.slotDetailSection}>
            <h2 className={styles.slotDetailHeading}>Key search</h2>
            <div className={styles.slotDetailSearchRow}>
              <Input
                value={matchInput}
                onChange={event => setMatchInput(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "Enter") handleSearch();
                }}
                placeholder="MATCH pattern e.g. doc:*"
                helperText="Redis glob: * matches any chars, ? one char, [abc] alternatives"
              />
              <Button variant="contained" color="primary" onClick={handleSearch}>
                Search
              </Button>
            </div>

            <div className={styles.slotDetailKeysHeader}>
              {scanning ? (
                <>
                  <span role="status">Scanning… {keys.length} found</span>
                  <Button variant="outlined" color="default" size="small" onClick={stopScan}>
                    Stop
                  </Button>
                </>
              ) : (
                <>
                  <span>
                    Showing {keys.length} keys
                    {!done && " · more available"}
                  </span>
                  {!done && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => void runScan(search.pattern)}
                    >
                      Load more
                    </Button>
                  )}
                </>
              )}
            </div>

            {keys.length === 0 && !scanning && (
              <p className={styles.slotDetailEmpty}>
                {done ? "No keys match this pattern." : "No matches yet."}
              </p>
            )}
            {keys.length > 0 && (
              <table className={styles.slotDetailTable}>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th className={styles.slotDetailColType}>Type</th>
                    <th className={styles.slotDetailColActions}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map(({ key, type }) => (
                    <tr
                      key={key}
                      className={selectedKey === key ? styles.slotDetailRowSelected : undefined}
                    >
                      <td className={styles.slotDetailKeyCell}>
                        <button
                          type="button"
                          className={styles.slotDetailLink}
                          onClick={() => handleSelectKey(key)}
                        >
                          {key}
                        </button>
                      </td>
                      <td className={styles.slotDetailColType}>{type}</td>
                      <td className={styles.slotDetailColActions}>
                        <Button
                          variant="text"
                          color="error"
                          size="small"
                          onClick={() => {
                            setDeleteTarget(key);
                            setDeleteConfirm("");
                          }}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {selectedKey && (
            <section className={styles.slotDetailSection}>
              <h2 className={styles.slotDetailHeading}>
                Key value
                <span className={styles.slotDetailHeadingNote}> · {selectedKey}</span>
                <CopyLabelButton value={selectedKey} />
              </h2>
              {keyValueLoading ? (
                <LoadingState message="Loading value..." />
              ) : keyValue ? (
                <>
                  <div className={styles.slotDetailValueMeta}>
                    <span>type: {keyValue.type}</span>
                    {keyValue.ttlSeconds >= 0 && <span>ttl: {keyValue.ttlSeconds}s</span>}
                    {keyValue.truncated && (
                      <span className={styles.slotDetailWarning}>truncated</span>
                    )}
                  </div>
                  <pre className={styles.slotDetailValue}>
                    {typeof keyValue.value === "string"
                      ? keyValue.value
                      : JSON.stringify(keyValue.value, null, 2)}
                  </pre>
                </>
              ) : null}
            </section>
          )}
        </>
      )}

      <Modal
        isOpen={deleteTarget !== null}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteTarget(null);
            setDeleteConfirm("");
          }
        }}
        title="Permanently delete this key?"
        size="medium"
        blurBackground
      >
        <div className={styles.modalBody}>
          <div className={styles.dangerBanner}>
            <strong>This action cannot be undone.</strong> The key will be permanently removed from
            keyspace <code>{keyspace}</code>. Active production traffic reading this key will see a
            cache miss.
          </div>

          <div className={styles.formField}>
            <label className={styles.fieldLabel} htmlFor="delete-confirm-key">
              Type the full key to confirm:
            </label>
            <code className={styles.dangerKeyDisplay}>{deleteTarget}</code>
            <Input
              id="delete-confirm-key"
              value={deleteConfirm}
              onChange={event => setDeleteConfirm(event.target.value)}
              placeholder="Paste or type the key here"
              autoFocus
            />
          </div>

          <div className={styles.actions}>
            <Button
              variant="text"
              color="default"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteConfirm("");
              }}
              disabled={deleteSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteConfirm !== deleteTarget || deleteSubmitting}
            >
              {deleteSubmitting ? "Deleting..." : "Permanently delete"}
            </Button>
          </div>
        </div>
      </Modal>

      {entry?.keyspace && (
        <TriggerSyncModal
          isOpen={triggerSyncOpen}
          initialKeyspace={entry.keyspace}
          family={entry.family}
          allKeyspaces={allKeyspaces}
          onClose={() => setTriggerSyncOpen(false)}
          onSubmitted={runId => {
            setTriggerSyncOpen(false);
            pushToast("success", `Sync triggered (run ${runId})`);
            refreshDetailState();
          }}
        />
      )}

      {entry && (
        <MakeActiveModal
          isOpen={makeActiveOpen}
          targetEntry={entry}
          currentActive={activeKeyspaces[entry.family]}
          missingPageTypes={missingPageTypes}
          targetedFlags={targetedFlags}
          onClose={() => setMakeActiveOpen(false)}
          onActivated={(newActive, warning) => {
            setActiveKeyspaces(prev => ({ ...prev, [entry.family]: newActive }));
            setMakeActiveOpen(false);
            pushToast(
              "success",
              warning
                ? `${newActive.mode === "slot" ? newActive.slot : "legacy"} is now active. ${warning}`
                : `${newActive.mode === "slot" ? newActive.slot : "legacy"} is now active.`,
            );
            refreshDetailState();
          }}
        />
      )}

      {entry?.keyspace && (
        <RemoveKeyspaceModal
          isOpen={removeOpen}
          keyspace={entry.keyspace}
          family={entry.family}
          onClose={() => setRemoveOpen(false)}
          onRemoved={() => {
            setRemoveOpen(false);
            pushToast("success", `Slot ${entry.keyspace} removed.`);
            navigate(ROUTES.l500Data.redisLoading);
          }}
        />
      )}

      {entry?.keyspace && (
        <PreviewLinkModal
          isOpen={previewLinkOpen}
          label={entry.keyspace}
          family={entry.family}
          onClose={() => setPreviewLinkOpen(false)}
        />
      )}

      <div className={styles.toastContainer}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`${styles.toast} ${
              toast.type === "success" ? styles.toastSuccess : styles.toastError
            }`}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
}
